import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, MessageCircle, Loader2 } from "lucide-react";
import { openWhatsApp } from "@/lib/whatsapp";

const STATUS_LABEL = {
  pending_payment: "Pending Payment",
  paid: "Paid",
  processing: "Processing",
  packed: "Packed",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderNumber}`).then(({ data }) => setOrder(data)).catch(() => setNotFound(true));
  }, [orderNumber]);

  if (notFound) return <div className="min-h-[60vh] flex items-center justify-center text-charcoal/60">Order not found.</div>;
  if (!order) return <div className="min-h-[60vh] flex items-center justify-center text-forest"><Loader2 className="animate-spin" /></div>;

  const paid = order.payment.status === "paid";
  const cod = order.payment.method === "cod";
  const a = order.address;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center">
        <div className="inline-flex h-16 w-16 rounded-full bg-forest/10 items-center justify-center">
          <CheckCircle2 className="text-forest" size={36} />
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-forest-dark mt-4" data-testid="order-confirmed-title">
          {paid || cod ? "Order Confirmed!" : "Order Received"}
        </h1>
        <p className="text-charcoal/65 mt-2">
          {cod ? "Your Cash on Delivery order is confirmed." : paid ? "Thank you! Your payment was successful." : "Your order is saved. Complete payment to confirm."}
        </p>
        <p className="font-mono text-sm text-forest mt-3 bg-wheat-pale inline-block px-4 py-1.5 rounded-full" data-testid="order-number">{order.order_number}</p>
      </div>

      <div className="bg-white rounded-2xl border border-forest/10 p-6 mt-8 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="font-serif text-lg text-forest-dark">Order Details</h2>
          <span className="text-xs font-semibold bg-forest/10 text-forest px-3 py-1 rounded-full" data-testid="order-status">{STATUS_LABEL[order.status]}</span>
        </div>
        <div className="divide-y divide-forest/10">
          {order.items.map((i) => (
            <div key={i.sku} className="flex justify-between py-3 text-sm">
              <span className="text-charcoal/80">{i.name} {i.pack_size} × {i.quantity}</span>
              <span className="font-semibold">{formatINR(i.line_total)}</span>
            </div>
          ))}
        </div>
        <div className="space-y-1.5 text-sm border-t border-forest/10 pt-4">
          <div className="flex justify-between"><span className="text-charcoal/70">Subtotal</span><span>{formatINR(order.subtotal)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-forest"><span>Discount</span><span>- {formatINR(order.discount)}</span></div>}
          <div className="flex justify-between"><span className="text-charcoal/70">Delivery</span><span>{order.shipping === 0 ? "FREE" : formatINR(order.shipping)}</span></div>
          <div className="flex justify-between font-bold text-forest-dark text-base pt-1"><span>{cod ? "Amount Due" : "Amount Paid"}</span><span data-testid="order-total">{formatINR(order.total)}</span></div>
          <p className="text-xs text-charcoal/50 pt-1">Payment: {cod ? "Cash on Delivery" : `Online · ${order.payment.status}`}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-forest/10 p-6 mt-4">
        <h2 className="font-serif text-lg text-forest-dark mb-2">Delivery Address</h2>
        <p className="text-sm text-charcoal/75">{order.customer.name} · {order.customer.phone}</p>
        <p className="text-sm text-charcoal/75 mt-1">{a.house}, {a.street}, {a.village}, {a.district}, {a.state} - {a.pincode}</p>
        <p className="text-xs text-charcoal/50 mt-3">Estimated delivery: shared over WhatsApp / phone as we dispatch your order.</p>
      </div>

      <div className="flex flex-wrap gap-3 mt-8 justify-center">
        <Link to="/"><Button variant="outline" className="border-forest/30 text-forest rounded-full h-11 px-6" data-testid="continue-shopping">Continue Shopping</Button></Link>
        <Button onClick={() => openWhatsApp({ name: order.customer.name })} className="bg-whatsapp text-white rounded-full h-11 px-6" data-testid="confirmation-whatsapp">
          <MessageCircle size={18} className="mr-2" /> Contact on WhatsApp
        </Button>
      </div>
    </div>
  );
}
